import Swal from 'sweetalert2';

const getValidationCriteria = (t) => ({
  role: {
    minLength: 20,
    mustInclude: ['you are', 'your role', 'act as'],
    suggestions: [
      t('promptValidator.suggestions.role.defineRole'),
      t('promptValidator.suggestions.role.includePersonality'),
      t('promptValidator.suggestions.role.specifyExpertise')
    ]
  },
  task: {
    minLength: 30,
    mustInclude: ['goal', 'objective', 'purpose', 'task'],
    suggestions: [
      t('promptValidator.suggestions.task.stateObjective'),
      t('promptValidator.suggestions.task.includeActions'),
      t('promptValidator.suggestions.task.defineOutcomes')
    ]
  },
  context: {
    minLength: 50,
    mustInclude: ['company', 'customer', 'service', 'product'],
    suggestions: [
      t('promptValidator.suggestions.context.provideContext'),
      t('promptValidator.suggestions.context.includeIndustry'),
      t('promptValidator.suggestions.context.describeAudience'),
      t('promptValidator.suggestions.context.specifyBackground')
    ]
  },
  rules: {
    minLength: 30,
    mustInclude: ['must', 'should', 'always', 'never'],
    suggestions: [
      t('promptValidator.suggestions.rules.includeGuidelines'),
      t('promptValidator.suggestions.rules.defineBoundaries'),
      t('promptValidator.suggestions.rules.specifyProtocols'),
      t('promptValidator.suggestions.rules.listRequirements')
    ]
  },
  forbidden_actions: {
    minLength: 20,
    mustInclude: ['never', 'do not', 'avoid', 'forbidden'],
    suggestions: [
      t('promptValidator.suggestions.forbiddenActions.listProhibited'),
      t('promptValidator.suggestions.forbiddenActions.includeRestrictions'),
      t('promptValidator.suggestions.forbiddenActions.specifyInformation'),
      t('promptValidator.suggestions.forbiddenActions.defineBoundaries')
    ]
  },
  default_fallback: {
    minLength: 15,
    suggestions: [
      t('promptValidator.suggestions.defaultFallback.provideEscalation'),
      t('promptValidator.suggestions.defaultFallback.includePolite'),
      t('promptValidator.suggestions.defaultFallback.defineRecovery')
    ]
  },
  example: {
    minLength: 40,
    mustInclude: ['example', 'conversation', 'dialogue'],
    suggestions: [
      t('promptValidator.suggestions.example.includeRealistic'),
      t('promptValidator.suggestions.example.showProper'),
      t('promptValidator.suggestions.example.demonstrate')
    ]
  }
});

const includesAnyTerm = (text, terms) => {
  return terms.some(term => text.toLowerCase().includes(term.toLowerCase()));
};

const generateHtmlContent = (results, t) => {
  let html = '<div style="text-align: left;">';
  
  Object.keys(results.fieldValidations).forEach(field => {
    const fieldValidation = results.fieldValidations[field];
    const fieldName = t(`promptValidator.fields.${field}`);
    
    html += `<h3 style="color: ${fieldValidation.isValid ? '#28a745' : '#dc3545'}; margin-top: 20px;">
      ${fieldName}: ${fieldValidation.isValid ? '✓' : '⚠️'}
    </h3>`;
    
    if (!fieldValidation.isValid) {
      html += '<ul style="margin-top: 5px; list-style: inside; font-size: 12px;">';
      fieldValidation.issues.forEach(issue => {
        html += `<li>${issue}</li>`;
      });
      html += '</ul>';
    }
  });

  if (!results.isValid) {
    html += `<h3 style="margin-top: 20px;">${t('promptValidator.suggestionsTitle')}:</h3><ul style="list-style: inside; font-size: 12px;">`;
    [...new Set(results.suggestions)].forEach(suggestion => {
      html += `<li>${suggestion}</li>`;
    });
    html += '</ul>';
  }

  html += '</div>';
  return html;
};

export const validatePrompt = (refs, t) => {
  const validationCriteria = getValidationCriteria(t);
  
  // Initialize results object
  const results = {
    isValid: true,
    suggestions: [],
    fieldValidations: {}
  };

  Object.keys(validationCriteria).forEach(field => {
    const fieldRef = refs[`contact_driver_${field}_ref`];
    const content = fieldRef?.current?.value || '';
    const criteria = validationCriteria[field];

    results.fieldValidations[field] = {
      isValid: true,
      issues: []
    };

    if (content.length < criteria.minLength) {
      results.fieldValidations[field].issues.push(
        t('promptValidator.validation.minLength', { 
          field: t(`promptValidator.fields.${field}`), 
          minLength: criteria.minLength 
        })
      );
      results.fieldValidations[field].isValid = false;
    }

    if (criteria.mustInclude && !includesAnyTerm(content, criteria.mustInclude)) {
      results.fieldValidations[field].issues.push(
        t('promptValidator.validation.mustInclude', { 
          field: t(`promptValidator.fields.${field}`), 
          terms: criteria.mustInclude.join(', ') 
        })
      );
      results.fieldValidations[field].isValid = false;
    }
  });

  results.isValid = Object.values(results.fieldValidations)
    .every(field => field.isValid);

  Object.keys(results.fieldValidations).forEach(field => {
    if (!results.fieldValidations[field].isValid) {
      results.suggestions.push(...validationCriteria[field].suggestions);
    }
  });

  Swal.fire({
    title: results.isValid ? t('promptValidator.titles.success') : t('promptValidator.titles.needsImprovement'),
    html: generateHtmlContent(results, t),
    icon: results.isValid ? 'success' : 'warning',
    confirmButtonText: t('promptValidator.confirmButton'),
    width: '600px'
  });

  return results;
};